import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, sectionHeadingSx, sectionGapSx, statusChipSx,
  tableHeadRowSx, headCellSx, rowSx, bodyCellSx, actionRowSx, dialogProps, dialogTitleSx,
} from '../portalStyles';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import {
  finishDisable, getStackSetsConfig, listRoleStatuses, listStackSetsAccounts, retryDisable,
} from '../../../services/stacksetsApi';
import { formatStatusLabel } from '../../../utils/statusLabel';
import StackSetsSetup from './StackSetsSetup';
import StackSetsList from './StackSetsList';
import RoleStatusPanel from './RoleStatusPanel';
import OperationProgress from '../securityhub/OperationProgress';
import { POLL_INTERVAL_MS } from '../securityhub/pollInterval';

const PAGE_TITLE = 'CloudFormation StackSets';
const PAGE_SUBTITLE = "Deploy templates to accounts across your organization's OUs";

// StackSetsEntry decides what to render at /stacksets:
//   no config / disabled / failed  → setup wizard
//   in_progress                    → transitioning view (polls until done)
//   disable_failed                 → Disable incomplete (retry removal / finish)
//   enabled / partial_failure      → StackSets list
export default function StackSetsEntry() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getStackSetsConfig();
      setConfig(res?.data || null);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll during any in_progress transition (enable or disable) so the view
  // advances automatically once the orchestrator goroutine finishes. Keyed on
  // config.status (a primitive), so the cleanup only fires on a real status
  // change or unmount — not on every poll tick.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    pollRef.current = setInterval(() => refresh({ showSpinner: false }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [config?.status, refresh]);

  // Loading and error keep the page header mounted so the layout does not jump
  // once the config lands — only the card body below it changes.
  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading StackSets…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => refresh()} />
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'in_progress') {
    const hint = (config.errorMessage || '').toLowerCase();
    const disabling = hint.includes('disabl');
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader
            icon={<LayersIcon sx={{ fontSize: 24 }} />}
            title={PAGE_TITLE}
            subtitle={disabling ? 'Disabling the service…' : 'Setting up the service…'}
          />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>
              {disabling ? 'Disabling StackSets…' : 'Setting up StackSets…'}
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              {disabling
                ? 'Removing execution roles from member accounts and cleaning up the admin account.'
                : 'Creating the administration role and template bucket, then provisioning an execution role in each account you selected. This can take a few minutes.'}
            </Typography>
            <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} />
          </Paper>
        </motion.div>
        {/* Per-account grid (docs: "the progress screen shows a per-account
            grid"). Retry is hidden here — the backend rejects retries until
            the fan-out finishes; failed rows become retryable on the list
            page once the config settles. */}
        <motion.div variants={sectionVariants}>
          <Box sx={sectionGapSx}>
            <RoleStatusPanel adminAccountId={config.adminAccountId} showRetry={false} />
          </Box>
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'disable_failed') {
    return <DisableIncompletePanel config={config} onChange={refresh} />;
  }

  if (!config || config.status === 'disabled' || config.status === 'failed') {
    return (
      <StackSetsSetup
        onComplete={refresh}
        priorFailure={config?.status === 'failed' ? config.errorMessage : null}
      />
    );
  }

  return <StackSetsList config={config} onConfigChange={refresh} />;
}

// DisableIncompletePanel is the disable_failed view: a disable removed some
// execution roles (or the admin-account teardown failed) and stopped. The
// only ways out are retrying the removal or dismissing the leftovers. Never
// the StackSets list, and never a role Retry, which would re-provision a role
// the disable is removing.
function DisableIncompletePanel({ config, onChange }) {
  const [rows, setRows] = useState(null);
  const [names, setNames] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await listRoleStatuses();
        setRows(res?.data || []);
      } catch (e) {
        setError(e.message);
        setRows([]);
      }
    })();
    (async () => {
      try {
        const res = await listStackSetsAccounts();
        const map = {};
        (res?.data || []).forEach((a) => { if (a.accountId) map[a.accountId] = a.name || ''; });
        setNames(map);
      } catch (_) {
        setNames({});
      }
    })();
  }, []);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChange({ showSpinner: false });
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const leftovers = (rows || []).filter((r) => r.phase === 'failed');
  const acctLabel = (id) => (names[id] ? `${names[id]} (${id})` : id);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title={PAGE_TITLE} subtitle="Disabling did not finish" />
      </motion.div>
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Disable incomplete</Typography>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
            {config.errorMessage || 'Some cleanup steps failed while disabling StackSets.'}
          </Alert>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>}
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 1 }}>
            {leftovers.length > 0
              ? 'These execution roles could not be removed:'
              : 'Every execution role was removed; the admin account cleanup is what remains.'}
          </Typography>
          {!rows ? (
            <PortalLoading label="Loading execution roles…" py={3} />
          ) : leftovers.length > 0 && (
            <TableContainer sx={{ mb: 2, border: `1px solid ${CK.border}`, borderRadius: '8px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={headCellSx}>Account</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leftovers.map((r, i) => (
                    <TableRow key={r.accountId} sx={rowSx(i)}>
                      <TableCell sx={bodyCellSx}>{acctLabel(r.accountId)}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, maxWidth: 480 }}>
                        <Chip size="small" label={formatStatusLabel(r.phase)} sx={statusChipSx('error')} />
                        {r.errorMessage && (
                          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.red, mt: 0.5, wordBreak: 'break-word' }}>
                            {r.errorMessage}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={actionRowSx}>
            <Button variant="contained" disabled={busy} onClick={() => run(retryDisable)}>
              Retry removal
            </Button>
            <Button disabled={busy} onClick={() => setConfirmOpen(true)}>
              Dismiss leftovers and finish
            </Button>
          </Box>
        </Paper>
      </motion.div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Dismiss leftover roles and finish</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1 }}>
            StackSets is marked disabled without touching AWS. Any execution role
            {leftovers.length > 0 ? ` left in ${leftovers.length} account${leftovers.length === 1 ? '' : 's'}` : ''}
            {' '}and anything the admin-account cleanup did not remove stay in place; delete them yourself if needed.
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1.5 }}>
            A leftover CKPrism-StackSetExecutionRole may still have AdministratorAccess attached.
            {leftovers.length > 0 ? ' Accounts:' : ''}
          </Typography>
          {leftovers.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {leftovers.map((r) => (
                <Typography key={r.accountId} component="li" sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body }}>
                  {acctLabel(r.accountId)}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setConfirmOpen(false); run(finishDisable); }}
          >
            Dismiss and finish
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
